import React, { useCallback, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { usePushNotifications } from '../../../hooks/pushNotification';
import svStrings from '../../../sv-strings';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import { useSegments } from '../../../hooks/useSegments';
import { Box, IconButton } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { SchedulePushdNotificationBody } from '../../../types/notification';
import PushNotificationDeleteModal from './components/push-notification-delete-modal';
import { NotificationStatsDialog } from './components/notification-stats';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 60px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  }),
};

interface ListEditProps {
  params: GridRenderCellParams;
  onDelete: (event: React.MouseEvent, notification: SchedulePushdNotificationBody) => void;
}

const ListEdit = ({ params, onDelete }: ListEditProps) => (
  <Box display="flex" justifyContent="center">
    <IconButton onClick={(event) => onDelete(event, params.row)}>
      <DeleteIcon color="error" />
    </IconButton>
  </Box>
);

const getWhenKey = (
  createdAt?: string,
  scheduledTime?: string
): 'whenImmediately' | 'whenScheduled' | null => {
  if (!createdAt || !scheduledTime) {
    return null;
  }

  const diff = new Date(scheduledTime).getTime() - new Date(createdAt).getTime();
  const IMMEDIATE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

  return diff < IMMEDIATE_THRESHOLD_MS ? 'whenImmediately' : 'whenScheduled';
};

const PushNotificationManual = () => {
  const { params } = useRouteMatch<{ abbrv?: string }>();
  const selectedAbbrv = params.abbrv || '';
  const [selectedTarget, setSelectedTarget] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const query = usePushNotifications(selectedAbbrv);
  const { query: segmentsQuery } = useSegments(selectedAbbrv);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<SchedulePushdNotificationBody | null>(null);
  const [selectedItemForStats, setSelectedItemForStats] = useState<any | null>(null);

  const { data, isLoading } = query;

  const { strings } = useStringsContext();
  const segments = segmentsQuery.data || [];
  const columnsScheduled: GridColDef[] = [
    {
      field: 'scheduledTime',
      headerName: svStrings.pushNotifications.fieldNameDate,
      width: 180,
      renderCell: (params: GridRenderCellParams) => moment(params.value).format('YYYY-MM-DD HH:mm'),
    },
    {
      field: 'when',
      headerName: svStrings.pushNotifications.when,
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        const whenKey = getWhenKey(params.row.createdAt, params.row.scheduledTime);

        if (!whenKey) {
          return '-';
        }

        const isImmediate = whenKey === 'whenImmediately';

        return (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            {isImmediate ? (
              <FlashOnIcon fontSize="small" color="warning" />
            ) : (
              <ScheduleIcon fontSize="small" color="info" />
            )}
            {strings.pushNotifications[whenKey]}
          </Box>
        );
      },
    },
    {
      field: 'createdBy',
      headerName: svStrings.pushNotifications.fieldNameAuthor,
      width: 250,
      renderCell: (params: GridRenderCellParams) =>
        ['Unknown', 'USER'].includes(params.value) ? '-' : params.value,
    },
    { field: 'app', headerName: svStrings.pushNotifications.fieldNameApp, width: 100 },
    {
      field: 'target',
      headerName: svStrings.pushNotifications.fieldNameTarget,
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.notification.target.type === 'ALL') {
          return strings.pushNotifications.allOption;
        }
        const segment = segments.find((s) => s.id === params.row.notification.target.value);
        return segment?.name || params.row.notification.target.value;
      },
    },

    {
      field: 'status',
      headerName: svStrings.pushNotifications.status,
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <FiberManualRecordIcon
            fontSize="small"
            color={(() => {
              if (params.value === 'SENT') {
                return 'success';
              }

              if (params.value === 'SCHEDULED') {
                return 'info';
              }

              if (params.value === 'FAILED') {
                return 'error';
              }

              return 'info';
            })()}
          />
          {strings.pushNotifications[params.value as keyof typeof strings.pushNotifications] ||
            params.value}
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: svStrings.pushNotifications.fieldNameType,
      width: 250,
      renderCell: (params: GridRenderCellParams) =>
        strings.pushNotifications[
          params.row.notification.type as keyof typeof strings.pushNotifications
        ] || params.row.notification.type,
    },
    {
      field: 'title',
      headerName: svStrings.pushNotifications.fieldNameTitle,
      width: 250,
      renderCell: (params: GridRenderCellParams) => params.row.notification.title,
    },
    {
      field: 'message',
      headerName: svStrings.pushNotifications.fieldNameMessage,
      minWidth: 250,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => params.row.notification.description,
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams<SchedulePushdNotificationBody>) => {
        return params.row.status === 'SCHEDULED' ? (
          <ListEdit params={params} onDelete={onShowDeleteModal} />
        ) : null;
      },
    },
  ];

  const onShowDeleteModal = useCallback(
    async (event: React.MouseEvent, row: SchedulePushdNotificationBody) => {
      event.stopPropagation();
      setSelectedNotification(row);
      setShowDeleteModal(true);
    },
    []
  );

  const onDeleteDone = useCallback(async () => {
    setShowDeleteModal(false);
    setSelectedNotification(null);
    query.refetch();
  }, [query]);

  const rows = (data || []).filter((row) => {
    if (!selectedTarget && !selectedStatus && !selectedType) {
      return true;
    }
    const filters = [];
    if (selectedTarget) {
      filters.push(row.notification.target.value === selectedTarget);
    }
    if (selectedStatus) {
      filters.push(row.status === selectedStatus);
    }
    if (selectedType) {
      filters.push(row.notification.type === selectedType);
    }
    return filters.every((f) => f);
  });

  const segmentsOptions = [
    {
      id: '',
      name: strings.pushNotifications.allOption,
    },
    ...segments,
  ];

  const statusOptions = [
    {
      id: '',
      name: strings.pushNotifications.allOption,
    },
    ...['SENT', 'SCHEDULED', 'FAILED'].map((id) => ({
      id,
      name: strings.pushNotifications[id as keyof typeof strings.pushNotifications],
    })),
  ];

  const typesOptions = [
    {
      id: '',
      name: strings.pushNotifications.allOption,
    },
    ...['ARTICLES', 'POLL', 'STORIES', 'MATCH', 'NEWS', 'WEB_PAGE'].map((id) => ({
      id,
      name: strings.pushNotifications[id as keyof typeof strings.pushNotifications],
    })),
  ];

  if (isLoading || segmentsQuery.isLoading) {
    return <></>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ minWidth: '200px' }}>
          <InputLabel size="small">{strings.pushNotifications.selectSegment}</InputLabel>
          <Select
            size="small"
            value={selectedTarget}
            onChange={(e: SelectChangeEvent) => {
              setSelectedTarget(e.target.value as string);
            }}
            label={
              segmentsOptions.find((s) => s.id === selectedTarget)?.name ||
              strings.pushNotifications.allOption
            }
          >
            {segmentsOptions.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: '200px' }}>
          <InputLabel size="small">{strings.pushNotifications.selectStatus}</InputLabel>
          <Select
            size="small"
            value={selectedStatus}
            onChange={(e: SelectChangeEvent) => {
              setSelectedStatus(e.target.value as string);
            }}
            label={
              statusOptions.find((s) => s.id === selectedStatus)?.name ||
              strings.pushNotifications.allOption
            }
          >
            {statusOptions.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: '200px' }}>
          <InputLabel size="small">{strings.pushNotifications.selectType}</InputLabel>
          <Select
            size="small"
            value={selectedType}
            onChange={(e: SelectChangeEvent) => {
              setSelectedType(e.target.value as string);
            }}
            label={
              typesOptions.find((s) => s.id === selectedType)?.name ||
              strings.pushNotifications.allOption
            }
          >
            {typesOptions.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rowHeight={40}
          rows={rows}
          columns={columnsScheduled}
          disableRowSelectionOnClick={false} // Enable row selection
          onRowClick={(params) => {
            setSelectedItemForStats(params.row);
          }}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'scheduledTime',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Paper>
      {showDeleteModal && selectedNotification && (
        <PushNotificationDeleteModal
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedNotification(null);
          }}
          onDone={onDeleteDone}
          selectedNotification={selectedNotification}
        />
      )}
      {selectedItemForStats ? (
        <NotificationStatsDialog
          open
          onClose={() => {
            setSelectedItemForStats(null);
          }}
          data={selectedItemForStats}
        />
      ) : null}
    </>
  );
};

export default PushNotificationManual;
